<!--
  场景树面板组件
  职责：显示剧情树、搜索/筛选、添加/删除剧情、拖拽排序
  数据来源：sceneEditor store（选中状态）、workspace store（工程数据引用）
-->
<script setup lang="ts">
import { ref, computed, watch, markRaw, h } from 'vue'
import {
  NDropdown,
  NInput,
  NTabPane,
  NTabs,
  useMessage,
} from 'naive-ui'
import type { DropdownOption, TreeOption } from 'naive-ui'
import AppTreeView from '@/components/AppTreeView.vue'
import CustomUITreePanel from '@/components/workspace/CustomUITreePanel.vue'
import TextInputDialog from '@/components/TextInputDialog.vue'
import { ENABLE_CUSTOM_UI_EDITOR } from '@/config/featureFlags'
import { filterStoryTree, type StoryTreeNode } from '@/features/story/storyTreeProjection'
import { useSceneEditorStore } from '@/store/sceneEditor'

const message = useMessage()
const editor = useSceneEditorStore()
const props = withDefaults(defineProps<{
  totalWordCount?: number
  wordCountByMapId?: Map<number, number>
}>(), {
  totalWordCount: 0,
  wordCountByMapId: () => new Map(),
})
const emit = defineEmits<{
  testScene: [mapId: number]
}>()
const activePanelTab = ref<'story' | 'ui'>('story')
const renameModalOpen = ref(false)
const renameDefaultName = ref('')
const renameTargetId = ref<number | null>(null)
const sceneCtxShow = ref(false)
const sceneCtxX = ref(0)
const sceneCtxY = ref(0)
const sceneCtxSceneId = ref<number | null>(null)
let lastTouchContextAt = 0

const scenePanelTabs = [
  { key: 'story', label: '剧情', hidden: false },
  { key: 'ui', label: 'UI界面', hidden: !ENABLE_CUSTOM_UI_EDITOR },
] as const

const visibleScenePanelTabs = computed(() => scenePanelTabs.filter((item) => !item.hidden))
const storyCount = computed(() => editor.storyTreeList.length)
const customUiCount = computed(() => {
  editor.customUiRevision
  return editor.customUiList.length
})
const totalWordCountText = computed(() => Math.max(0, Math.floor(props.totalWordCount)).toLocaleString('zh-CN'))
const currentMapWordCountText = computed(() => {
  const mapId = editor.selectedSceneId
  if (mapId === null) return '0'
  const count = props.wordCountByMapId.get(mapId) ?? 0
  return Math.max(0, Math.floor(count)).toLocaleString('zh-CN')
})
/** 筛选输入（实时） */
const storyFilter = ref('')

/** 防抖后的筛选串，避免 1000+ 节点时每次按键全量重建过滤树 */
const storyFilterDebounced = ref('')
let storyFilterDebounceTimer: ReturnType<typeof setTimeout> | null = null

watch(
  storyFilter,
  (v) => {
    if (storyFilterDebounceTimer) clearTimeout(storyFilterDebounceTimer)
    storyFilterDebounceTimer = setTimeout(() => {
      storyFilterDebounced.value = v
      storyFilterDebounceTimer = null
    }, 120)
  },
  { immediate: true },
)

watch(
  activePanelTab,
  (tab) => {
    if (tab === 'story') {
      editor.ensureStorySelection()
      return
    }
    if (ENABLE_CUSTOM_UI_EDITOR) editor.ensureCustomUiSelection()
  },
)

watch(
  customUiCount,
  () => {
    if (ENABLE_CUSTOM_UI_EDITOR && activePanelTab.value === 'ui') {
      editor.ensureCustomUiSelection()
    }
  },
)

/* ——— n-tree 数据源 ——— */

const filteredStoryTree = computed(() => {
  const q = storyFilterDebounced.value.trim().toLowerCase()
  if (!q) return editor.storyTreeData
  return filterStoryTree(editor.storyTreeData as StoryTreeNode[], q)
})

/** 筛选非空时禁止拖拽 */
const storyTreeDraggable = computed(
  () => !storyFilterDebounced.value.trim() && !!editor.storyTreeList.length,
)

/* ——— 选中与展开 ——— */

const selectedSceneKeys = computed<Array<string | number>>(() =>
  editor.selectedSceneId === null ? [] : [editor.selectedSceneId],
)

function onSceneTreeSelect(keys: Array<string | number>) {
  const key = keys[0]
  if (key === undefined || key === null) return
  const nextId = Number(key)
  if (Number.isFinite(nextId)) editor.selectScene(nextId)
}

/* ——— 拖拽 ——— */

function allowStoryDrop({
  dropPosition,
  node,
}: {
  dropPosition: 'before' | 'inside' | 'after'
  node: TreeOption
  phase: 'drag' | 'drop'
}): boolean {
  return editor.allowStoryDrop(
    editor.storyDragFromId,
    Number(node.key),
    dropPosition,
  )
}

function onStoryDragStart(info: { node: TreeOption }) {
  editor.storyDragFromId = Number(info.node.key)
}

function onStoryDragEnd() {
  editor.storyDragFromId = null
}

function onStoryDrop(info: {
  node: TreeOption
  dragNode: TreeOption
  dropPosition: 'before' | 'inside' | 'after'
}) {
  const dragId = Number(info.dragNode.key)
  const targetId = Number(info.node.key)
  editor.storyDragFromId = null
  if (!Number.isFinite(dragId) || !Number.isFinite(targetId)) return
  const ok = editor.handleStoryDrop(dragId, targetId, info.dropPosition)
  if (!ok) message.warning('无法将剧情移动到此处')
}

/* ——— 增删剧情 ——— */

function onAddStoryMap() {
  editor.addStoryMap()
}

function onAddChildStoryMap(id: number = editor.selectedSceneId ?? 0) {
  const nm = editor.addChildStoryMap(id)
  if (!nm) message.warning('无法添加子剧情')
}

function onDeleteStoryMap(id: number | null = editor.selectedSceneId) {
  if (id === null) {
    message.warning('请先选择要删除的剧情')
    return
  }
  if (!window.confirm('确定删除该剧情及其子剧情？此操作不可撤销。')) return
  const ok = editor.deleteCurrentStoryMap(id)
  if (!ok) message.warning('至少保留一条剧情')
}

function openRenameModal(id: number) {
  const target = editor.storyTreeList.find((item) => item.Id === id)
  if (!target) {
    message.warning('未找到要重命名的剧情')
    return
  }
  renameTargetId.value = id
  renameDefaultName.value = target.Name?.trim() || ''
  renameModalOpen.value = true
}

function confirmRenameScene(value: string) {
  const id = renameTargetId.value
  const nextName = value.trim()
  if (id === null) return false
  if (!nextName) {
    message.warning('请输入剧情名称')
    return false
  }
  const ok = editor.renameScene(id, nextName)
  if (!ok) {
    message.error('剧情重命名失败')
    return false
  }
  renameTargetId.value = null
  return true
}

const sceneCtxOptions = computed<DropdownOption[]>(() => {
  if (sceneCtxSceneId.value === null) {
    return [
      { label: '添加剧情', key: 'add-root' },
      { label: '粘贴', key: 'paste', disabled: !editor.hasStoryClipboard },
    ]
  }
  const canRemoveScene = sceneCtxSceneId.value !== null && editor.canDeleteStoryMap(sceneCtxSceneId.value)
  return [
    { label: '添加剧情', key: 'add-sibling' },
    { label: '添加子剧情', key: 'add-child' },
    { label: '复制', key: 'copy' },
    { label: '剪切', key: 'cut', disabled: !canRemoveScene },
    { label: '粘贴', key: 'paste', disabled: !editor.hasStoryClipboard },
    { label: '重命名', key: 'rename' },
    { label: '删除', key: 'delete', disabled: !canRemoveScene },
    { label: '测试', key: 'test' },
  ]
})

function openSceneContextAt(x: number, y: number, sceneId: number | null) {
  sceneCtxSceneId.value = sceneId
  if (sceneId !== null) {
    editor.selectScene(sceneId)
  }
  sceneCtxX.value = x
  sceneCtxY.value = y
  sceneCtxShow.value = true
}

function openSceneContextMenu(event: MouseEvent, sceneId: number | null) {
  event.preventDefault()
  event.stopPropagation()
  if (isTouchGeneratedContextMenu(event)) return
  openSceneContextAt(event.clientX, event.clientY, sceneId)
}

function onSceneTreeBlankContextMenu(event: MouseEvent) {
  const target = event.target
  if (target instanceof Element && target.closest('.n-tree-node')) return
  openSceneContextMenu(event, null)
}

function isTouchGeneratedContextMenu(event: MouseEvent): boolean {
  const sourceCapabilities = (event as MouseEvent & { sourceCapabilities?: { firesTouchEvents?: boolean } }).sourceCapabilities
  return Boolean(sourceCapabilities?.firesTouchEvents) || performance.now() - lastTouchContextAt < 900
}

function rememberTouchContext(event: PointerEvent): void {
  if (event.pointerType !== 'mouse') {
    lastTouchContextAt = performance.now()
  }
}

function openSceneMoreMenu(event: MouseEvent, sceneId: number) {
  event.preventDefault()
  event.stopPropagation()
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  openSceneContextAt(rect.right, rect.bottom, sceneId)
}

function renderSceneLabel({ option }: { option: TreeOption }) {
  const sceneId = Number(option.key)
  const isSelected = selectedSceneKeys.value.includes(sceneId)
  return h('span', { class: 'scene-node-label' }, [
    h('span', { class: 'scene-node-label__text' }, String(option.label ?? '')),
    isSelected
      ? h(
      'span',
      {
        class: 'scene-node-more',
        role: 'button',
        tabindex: 0,
        title: '更多操作',
        'aria-label': '更多操作',
        onClick: (event: MouseEvent) => openSceneMoreMenu(event, sceneId),
        onPointerdown: (event: PointerEvent) => event.stopPropagation(),
      },
      '更多',
      )
      : null,
  ])
}

function sceneTreeNodeProps({ option }: { option: TreeOption }) {
  const sceneId = Number(option.key)
  return {
    onContextmenu: (event: MouseEvent) => openSceneContextMenu(event, sceneId),
    onPointerdown: (event: PointerEvent) => rememberTouchContext(event),
  }
}

function onSceneCtxSelect(key: string | number) {
  sceneCtxShow.value = false
  const sceneId = sceneCtxSceneId.value
  switch (key) {
    case 'add-root':
      editor.addStoryMap(null)
      break
    case 'add-sibling':
      if (sceneId === null) return
      editor.addStoryMap(sceneId)
      break
    case 'add-child':
      if (sceneId === null) return
      onAddChildStoryMap(sceneId)
      break
    case 'rename':
      if (sceneId === null) return
      openRenameModal(sceneId)
      break
    case 'copy':
      if (sceneId === null || !editor.copyStoryMap(sceneId)) message.warning('复制剧情失败')
      break
    case 'cut':
      if (sceneId === null || !editor.cutStoryMap(sceneId)) message.warning('至少保留一条剧情')
      break
    case 'paste':
      if (!editor.pasteStoryMap(sceneId)) message.warning('没有可粘贴的剧情')
      break
    case 'delete':
      onDeleteStoryMap(sceneId)
      break
    case 'test':
      if (sceneId !== null) {
        editor.selectScene(sceneId)
        emit('testScene', sceneId)
      }
      break
  }
}
</script>

<template>
  <aside class="col-scene" @contextmenu.prevent>
    <n-tabs v-model:value="activePanelTab" type="segment" size="small" :animated="false" class="scene-tabs">
      <n-tab-pane
        v-for="tab in visibleScenePanelTabs"
        :key="tab.key"
        :name="tab.key"
        :tab="tab.key === 'story' ? `剧情[${storyCount}]` : `UI界面[${customUiCount}]`"
      >
        <template v-if="tab.key === 'story'">
          <div class="scene-filter-row">
            <n-input
              v-model:value="storyFilter"
              size="small"
              clearable
              placeholder="筛选剧情"
              class="scene-filter"
            />
            <button type="button" class="scene-add-button" @click="onAddStoryMap">新剧情</button>
          </div>

          <AppTreeView
            :allow-drop="allowStoryDrop"
            :node-props="sceneTreeNodeProps"
            :render-label="renderSceneLabel"
            :data="filteredStoryTree"
            :expanded-keys="editor.expandedSceneKeys"
            :selected-keys="selectedSceneKeys"
            :draggable="storyTreeDraggable"
            @contextmenu="onSceneTreeBlankContextMenu"
            @pointerdown="rememberTouchContext"
            @update:expanded-keys="editor.expandedSceneKeys = $event"
            @update:selected-keys="onSceneTreeSelect"
            @dragstart="onStoryDragStart"
            @dragend="onStoryDragEnd"
            @drop="onStoryDrop"
          />
        </template>
        <template v-else-if="ENABLE_CUSTOM_UI_EDITOR && tab.key === 'ui'">
          <CustomUITreePanel />
        </template>
      </n-tab-pane>
    </n-tabs>

    <div v-if="activePanelTab === 'story'" class="scene-status-bar">
      <span>总字数：{{ totalWordCountText }}</span>
      <span>当前剧情：{{ currentMapWordCountText }}</span>
    </div>

    <n-dropdown
      trigger="manual"
      placement="bottom-start"
      :show="sceneCtxShow"
      :x="sceneCtxX"
      :y="sceneCtxY"
      :options="sceneCtxOptions"
      @select="onSceneCtxSelect"
      @clickoutside="sceneCtxShow = false"
    />

    <TextInputDialog
      v-model:show="renameModalOpen"
      title="重命名剧情"
      :model-value="renameDefaultName"
      placeholder="请输入剧情名称"
      :confirm="confirmRenameScene"
    />
  </aside>
</template>

<style scoped>
.col-scene {
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: var(--theme-bg-panel);
  border-right: 0;
}

.scene-tabs {
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
}

.scene-status-bar {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: 5px var(--app-space-sm);
  border-top: 1px solid var(--theme-border);
  color: var(--theme-text-secondary);
  background: var(--theme-bg-panel);
  font-size: var(--app-font-size-xs);
}

.scene-tabs :deep(.n-tabs-pane-wrapper),
.scene-tabs :deep(.n-tabs-content),
.scene-tabs :deep(.n-tab-pane) {
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  flex-direction: column;
}

.scene-tabs :deep(.n-tabs-nav) {
  flex: 0 0 auto;
  padding: 4px 6px 0;
}

.scene-tabs :deep(.n-tabs-tab__label),
.scene-filter :deep(.n-input__input-el),
.scene-filter :deep(.n-input__placeholder) {
  font-size: var(--app-font-size-sm);
}

.scene-filter-row {
  display: flex;
  gap: var(--app-space-xs);
  padding: var(--app-space-sm) var(--app-space-sm) var(--app-space-xs);
}

.scene-filter {
  flex: 1;
  min-width: 0;
}

.scene-filter :deep(.n-input-wrapper) {
  background: var(--theme-bg-card);
  border-color: var(--theme-border);
}

.scene-add-button {
  border: 0;
  color: var(--theme-text-secondary);
  background: var(--theme-bg-secondary);
  font-size: var(--app-font-size-xs);
  cursor: pointer;
  appearance: none;
}

.scene-add-button {
  flex: 0 0 auto;
  padding: 0 var(--app-space-sm);
}

.scene-tabs :deep(.scene-node-label) {
  display: inline-flex;
  align-items: center;
  gap: var(--app-space-xs);
  min-width: 0;
}

.scene-tabs :deep(.scene-node-label__text) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scene-tabs :deep(.scene-node-more) {
  all: unset;
  color: var(--theme-text-very-muted);
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
}

.scene-tabs :deep(.scene-node-more:hover),
.scene-tabs :deep(.scene-node-more:focus-visible) {
  color: var(--theme-text-secondary);
}

@media (hover: hover) and (pointer: fine) {
  .scene-tabs :deep(.scene-node-more:focus-visible) {
    display: inline-block;
  }
}

</style>
